import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  api,
  structureReportPath,
  type StructureDetail,
  type StructureType,
  type StructureWrite,
} from '../lib/api';
import {
  StructureNotesPhotosFields,
  uploadStructurePhotos,
} from '../lib/StructureNotesPhotosFields';
import { clearUnsavedChanges } from '../lib/UnsavedChanges';
import { HoverTooltip } from '../lib/HoverTooltip';
import { SubtypeInfoModal } from './SubtypeInfoModal';

type Family = 'precast' | 'castiron' | 'curbthroat';

interface SubtypeTile {
  type: StructureType;
  label: string;
  blurb: string;
  image: string;
}

interface FieldSpec {
  key: keyof StructureWrite;
  label: string;
  /** HTML `min` attribute (legacy parity). */
  min: number;
  /** snake_case id used by the SVG `{field}_output` text nodes (legacy parity). */
  svgId: string;
}

const FIELD_SPEC: Record<string, { key: keyof StructureWrite; min: number }> = {
  grate_size_left_to_right: { key: 'grateSizeLeftToRight', min: 12 },
  grate_size_front_to_back: { key: 'grateSizeFrontToBack', min: 12 },
  clear_opening_left_to_right: { key: 'clearOpeningLeftToRight', min: 12 },
  clear_opening_front_to_back: { key: 'clearOpeningFrontToBack', min: 12 },
  grate_size_long_span: { key: 'grateSizeLongSpan', min: 12 },
  grate_size_short_span: { key: 'grateSizeShortSpan', min: 12 },
  clear_opening_long_span: { key: 'clearOpeningLongSpan', min: 12 },
  clear_opening_short_span: { key: 'clearOpeningShortSpan', min: 12 },
  curb_opening_width: { key: 'curbOpeningWidth', min: 12 },
  catch_basin_wall_to_wall_inside_dimension: {
    key: 'catchBasinWallToWallInsideDimension',
    min: 14,
  },
  grate_diameter: { key: 'grateDiameter', min: 14 },
  frame_clear_opening_diameter: { key: 'frameClearOpeningDiameter', min: 12 },
  basin_size: { key: 'basinSize', min: 0 },
};

function parseEnabledFields(json: string, type: StructureType): FieldSpec[] {
  if (!json) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return [];
  }
  if (!parsed || typeof parsed !== 'object') return [];
  const castIronGrateSize =
    type.isCastIronRectangular || type.isCastIronCurbBoxCombo;
  return Object.entries(parsed as Record<string, string>)
    .map(([snake, label]) => {
      const spec = FIELD_SPEC[snake];
      if (!spec) return null;
      // Legacy parity: cast iron flat-rectangular & curb-box grate-size inputs
      // enforce a 14" minimum (precast uses 12").
      const min =
        castIronGrateSize &&
        (snake === 'grate_size_left_to_right' ||
          snake === 'grate_size_front_to_back')
          ? 14
          : spec.min;
      return { key: spec.key, label, min, svgId: snake };
    })
    .filter((f): f is FieldSpec => !!f);
}

function tileImage(type: StructureType): string {
  if (type.is4SeatSquare) return '/images/PRODUCT/Precast-4Seat.png';
  if (type.is2SeatLong) return '/images/PRODUCT/Precast-2SeatLong.png';
  if (type.is2SeatShort) return '/images/PRODUCT/Precast-2SeatShort.png';
  if (type.isCastIronRound) return '/images/PRODUCT/CastIron-Round.png';
  if (type.isCastIronRectangular) return '/images/PRODUCT/CastIron-FlatRectangular.png';
  if (type.isCastIronCurbBoxCombo) return '/images/PRODUCT/CastIron-CurbBox.png';
  if (type.isOpenThroat) return '/images/PRODUCT/Product-OpenThroat.png';
  return '/images/PRODUCT/Product-CastIron.png';
}

function diagramSlugFor(type: StructureType): string {
  if (type.is4SeatSquare) return 'Precast-4Seat';
  if (type.is2SeatLong) return 'Precast-2SeatLong';
  if (type.is2SeatShort) return 'Precast-2SeatShort';
  if (type.isCastIronRound) return 'CastIron-Round';
  if (type.isCastIronRectangular) return 'CastIron-Rectangular';
  if (type.isCastIronCurbBoxCombo) return 'CastIron-CurbBox';
  if (type.isOpenThroat) return 'CurbThroat';
  return '';
}

/**
 * Per-subtype configuration heading. Mirrors the legacy dedicated form pages
 * (`resources/jsx/components/forms/*forms.jsx`), each of which had its own
 * `<h1>` rather than the generic family title.
 */
function configTitleFor(type: StructureType): string {
  if (type.is4SeatSquare)
    return 'Precast Concrete Structure with Grate\u00a0Configuration';
  if (type.is2SeatLong)
    return 'Rectangular Grate 2 Seat Support (Long\u00a0Span) Structure\u00a0Configuration';
  if (type.is2SeatShort)
    return 'Rectangular Grate 2 Seat Support (Short\u00a0Span) Structure\u00a0Configuration';
  if (type.isCastIronRound)
    return 'Round Frame and Grate Cast Iron\u00a0Inlet Structure\u00a0Configuration';
  if (type.isCastIronRectangular)
    return 'Flat-Rectangular Frame and Grate Cast Iron\u00a0Inlet Structure\u00a0Configuration';
  if (type.isCastIronCurbBoxCombo)
    return 'Combination Curb\u00a0Inlet Structure\u00a0Configuration';
  if (type.isOpenThroat)
    return 'Open Throat Curb\u00a0Inlet Structure\u00a0Configuration';
  return type.name;
}

function subtypeTilesFor(family: Family, types: StructureType[]): SubtypeTile[] {
  const valid = types.filter(
    (t) => t.family !== 'INVALID STRUCTURE' && t.name !== 'INVALID STRUCTURE',
  );
  if (family === 'precast') {
    const want = valid.filter((t) => t.is4SeatSquare);
    return want.map((type) => ({
      type,
      label: '4-Seat Square',
      blurb: type.name,
      image: tileImage(type),
    }));
  }
  if (family === 'curbthroat') {
    const want = valid.filter((t) => t.isOpenThroat);
    return want.map((type) => ({
      type,
      label: 'Open Throat Curb Inlet',
      blurb: type.name,
      image: tileImage(type),
    }));
  }
  // Legacy parity: fixed subtype order Round → Flat-Rectangular → Curb Box
  // (see resources/jsx/components/page2.jsx "Cast Iron Frame and Grate Selection").
  const castIronRank = (t: StructureType) =>
    t.isCastIronRound ? 0 : t.isCastIronRectangular ? 1 : t.isCastIronCurbBoxCombo ? 2 : 3;
  const want = valid
    .filter(
      (t) =>
        t.isCastIronRound ||
        t.isCastIronRectangular ||
        t.isCastIronCurbBoxCombo,
    )
    .sort((a, b) => castIronRank(a) - castIronRank(b));
  return want.map((type) => ({
    type,
    label: type.isCastIronRound
      ? 'Round Frame and Grate'
      : type.isCastIronRectangular
        ? 'Flat-Rectangular Frame and\u00a0Grate'
        : 'Curb Box Combination',
    blurb: type.name,
    image: tileImage(type),
  }));
}

/**
 * Step 3 dimensions form for Precast / Cast Iron / Curb Throat families.
 * Markup mirrors `resources/jsx/components/forms/precastconcrete4seatforms.jsx`
 * etc. (Bootstrap 5 + Hyper theme).
 */
export function WizardDimensionsForm({ family }: { family: Family }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isPermanent = params.get('temp') !== 'true';
  const oilFiltration = isPermanent && params.get('oil') === 'true';

  const editIdStr = params.get('id');
  const editId = editIdStr?.trim() || null;
  const isEdit = !!editId;

  const existingQuery = useQuery({
    queryKey: ['structure', editId],
    queryFn: () => api.get<StructureDetail>(`/api/v1/structures/${editId}`),
    enabled: isEdit,
  });

  const typesQuery = useQuery({
    queryKey: ['structure-types'],
    queryFn: () => api.get<StructureType[]>('/api/v1/structure-types'),
  });

  const tiles = useMemo(
    () => subtypeTilesFor(family, typesQuery.data ?? []),
    [family, typesQuery.data],
  );

  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
  const selectedTile = tiles.find((t) => t.type.id === selectedTypeId) ?? null;
  const [infoTypeId, setInfoTypeId] = useState<number | null>(null);
  const infoTile = tiles.find((t) => t.type.id === infoTypeId) ?? null;

  useEffect(() => {
    if (tiles.length === 1 && selectedTypeId === null) {
      setSelectedTypeId(tiles[0].type.id);
    }
  }, [tiles, selectedTypeId]);

  const [nickname, setNickname] = useState('');
  const [values, setValues] = useState<Partial<StructureWrite>>({});
  const supportsHeavyDuty = family !== 'curbthroat';
  const [heavyDuty, setHeavyDuty] = useState<boolean | null>(null);
  const [extendedBottomDepth, setExtendedBottomDepth] = useState<number | ''>('');
  const [extendedBottomWidth, setExtendedBottomWidth] = useState<number | ''>('');
  const [notes, setNotes] = useState('');
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [showExtras, setShowExtras] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mintriggered, setMintriggered] = useState(false);

  useEffect(() => {
    if (!isEdit || !existingQuery.data || tiles.length === 0) return;
    if (selectedTypeId !== null) return;
    const s = existingQuery.data;
    const matching = tiles.find((t) => t.type.id === s.structureTypeId);
    if (matching) setSelectedTypeId(matching.type.id);
    setNickname(s.nickname ?? '');
    setHeavyDuty(s.extendedBottomHeavyDuty);
    setExtendedBottomDepth(s.extendedBottomDepth ?? '');
    setExtendedBottomWidth(s.extendedBottomWidth ?? '');
    setNotes(s.structureNotes ?? '');
    const next: Partial<StructureWrite> = {};
    const copy = (k: keyof StructureWrite) => {
      const v = (s as unknown as Record<string, unknown>)[k];
      if (typeof v === 'number') next[k] = v as never;
    };
    for (const spec of Object.values(FIELD_SPEC)) copy(spec.key);
    setValues(next);
  }, [isEdit, existingQuery.data, tiles, selectedTypeId]);

  const enabledFields = useMemo(
    () => (selectedTile ? parseEnabledFields(selectedTile.type.enabledFields, selectedTile.type) : []),
    [selectedTile],
  );

  const svgRef = useRef<HTMLObjectElement>(null);
  const svgMobileRef = useRef<HTMLObjectElement>(null);

  // Legacy parity (precastconcrete*forms.jsx etc.): the measurement diagram is
  // an embedded SVG whose `<text id="{field}_output">` nodes are filled in with
  // the entered dimensions. This only works when the SVG is embedded via
  // `<object>` (so its inner DOM is reachable), not an `<img>`.
  const writeOutputs = useCallback(() => {
    for (const obj of [svgRef.current, svgMobileRef.current]) {
      const doc = obj?.contentDocument;
      if (!doc) continue;
      for (const f of enabledFields) {
        const v = values[f.key];
        const text = typeof v === 'number' && Number.isFinite(v) ? `${v} inches` : '';
        const out1 = doc.getElementById(`${f.svgId}_output`);
        if (out1) out1.textContent = text;
        const out2 = doc.getElementById(`${f.svgId}_output_2`);
        if (out2) out2.textContent = text;
      }
    }
  }, [enabledFields, values]);

  useEffect(() => {
    writeOutputs();
  }, [writeOutputs]);

  // Legacy parity (precastconcrete*forms.jsx handleFocus/handleHover): focusing
  // or hovering a dimension field lights up the matching label on the left-hand
  // measurement diagram green (#73a300). The SVG label nodes are `{field}_label`
  // and `{field}_label_2`; only one field is highlighted at a time.
  const highlightedRef = useRef<SVGElement[]>([]);
  const highlightField = useCallback((svgId: string | null) => {
    for (const el of highlightedRef.current) el.style.fill = 'black';
    highlightedRef.current = [];
    if (!svgId) return;
    const next: SVGElement[] = [];
    for (const obj of [svgRef.current, svgMobileRef.current]) {
      const doc = obj?.contentDocument;
      if (!doc) continue;
      for (const suffix of ['_label', '_label_2']) {
        const el = doc.getElementById(`${svgId}${suffix}`) as SVGElement | null;
        if (el) {
          el.style.fill = '#73a300';
          next.push(el);
        }
      }
    }
    highlightedRef.current = next;
  }, []);

  useEffect(() => {
    if (isEdit && selectedTypeId === null) return;
    if (isEdit && existingQuery.data?.structureTypeId === selectedTypeId) return;
    setValues({});
    setHeavyDuty(null);
    setExtendedBottomDepth('');
    setExtendedBottomWidth('');
    setMintriggered(false);
  }, [selectedTypeId, isEdit, existingQuery.data?.structureTypeId]);

  const crossFieldErrors = useMemo<string[]>(() => {
    const out: string[] = [];
    const num = (k: keyof StructureWrite) => Number(values[k] ?? 0);
    // Legacy parity: cast iron flat-rectangular & curb-box enforce a 2" ledge
    // rule between grate size and clear opening; precast uses a plain "greater
    // than" check.
    const needsLedge =
      !!selectedTile &&
      (selectedTile.type.isCastIronRectangular ||
        selectedTile.type.isCastIronCurbBoxCombo);
    const gsl = num('grateSizeLeftToRight');
    const col = num('clearOpeningLeftToRight');
    if (gsl > 0 && col > 0) {
      if (needsLedge) {
        if (gsl < col) {
          out.push('Grate Size - Left to Right must be larger than Clear Opening - Left to Right.');
        } else if (gsl - 2 < col) {
          out.push(
            'Grate Size - Left to Right must be at least 2 inches greater than Clear Opening - Left to Right. Ledge is not wide enough to support a FlexStorm filter.',
          );
        }
      } else if (gsl < col) {
        out.push('Grate Size - Left to Right must be greater than Clear Opening - Left to Right.');
      }
    }
    const gsf = num('grateSizeFrontToBack');
    const cof = num('clearOpeningFrontToBack');
    if (gsf > 0 && cof > 0) {
      if (needsLedge) {
        if (gsf < cof) {
          out.push('Grate Size - Front to Back must be larger than Clear Opening - Front to Back.');
        } else if (gsf - 2 < cof) {
          out.push(
            'Grate Size - Front to Back must be at least 2 inches greater than Clear Opening - Front to Back. Ledge is not wide enough to support a FlexStorm filter.',
          );
        }
      } else if (gsf < cof) {
        out.push('Grate Size - Front to Back must be greater than Clear Opening - Front to Back.');
      }
    }
    if (
      num('grateSizeLongSpan') > 0 &&
      num('clearOpeningLongSpan') > 0 &&
      num('grateSizeLongSpan') < num('clearOpeningLongSpan')
    ) {
      out.push('Grate Size - Long Span must be greater than Clear Opening - Long Span.');
    }
    if (
      num('grateSizeShortSpan') > 0 &&
      num('clearOpeningShortSpan') > 0 &&
      num('grateSizeShortSpan') < num('clearOpeningShortSpan')
    ) {
      out.push('Grate Size - Short Span must be greater than Clear Opening - Short Span.');
    }
    const gd = num('grateDiameter');
    const fd = num('frameClearOpeningDiameter');
    if (gd > 0 && fd > 0) {
      if (gd < fd) {
        out.push('Grate Diameter must be larger than Frame Clear Opening Diameter.');
      } else if (gd - 2 < fd) {
        out.push(
          'Grate Diameter must be at least 2 inches greater than Frame Clear Opening Diameter. Ledge is not wide enough to support a FlexStorm filter.',
        );
      }
    }
    if (heavyDuty === true) {
      if (!(typeof extendedBottomDepth === 'number' && extendedBottomDepth > 0)) {
        out.push('Extended Bottom Depth is required for Heavy Duty configurations.');
      }
      if (!(typeof extendedBottomWidth === 'number' && extendedBottomWidth > 0)) {
        out.push('Extended Bottom Width is required for Heavy Duty configurations.');
      }
    }
    return out;
  }, [values, heavyDuty, extendedBottomDepth, extendedBottomWidth, selectedTile]);

  const allFieldsFilled =
    enabledFields.length === 0 ||
    enabledFields.every((f) => {
      const v = values[f.key];
      return typeof v === 'number' && v > 0;
    });
  const grateStyleChosen = !supportsHeavyDuty || heavyDuty !== null;
  const canSubmit =
    !!selectedTile &&
    allFieldsFilled &&
    grateStyleChosen &&
    crossFieldErrors.length === 0 &&
    !submitting;

  const submit = async () => {
    if (!canSubmit || !selectedTile) return;
    setSubmitting(true);
    setError(null);
    try {
      const body: StructureWrite = {
        nickname: nickname.trim() || null,
        permanent: isPermanent,
        oilFiltration,
        structureTypeId: selectedTile.type.id,
        grateStyleId: isEdit ? (existingQuery.data?.grateStyleId ?? null) : null,
        foundryId: isEdit ? (existingQuery.data?.foundryId ?? null) : null,
        foundryPartId: isEdit ? (existingQuery.data?.foundryPartId ?? null) : null,
        extendedBottomHeavyDuty: supportsHeavyDuty && heavyDuty === true,
        extendedBottomDepth:
          supportsHeavyDuty && heavyDuty === true && typeof extendedBottomDepth === 'number'
            ? extendedBottomDepth
            : null,
        extendedBottomWidth:
          supportsHeavyDuty && heavyDuty === true && typeof extendedBottomWidth === 'number'
            ? extendedBottomWidth
            : null,
        structureNotes: notes.trim() || null,
        // Legacy parity: the `isdraft` column defaults to true, so a freshly
        // generated structure is a draft (surfacing the Close Structure modal).
        // Editing an existing structure never changes its draft state.
        isDraft: isEdit ? (existingQuery.data?.isDraft ?? true) : true,
        ...values,
      };
      if (isEdit && editId) {
        const saved = await api.put<StructureDetail>(`/api/v1/structures/${editId}`, body);
        queryClient.setQueryData(['structure', editId], saved);
        await uploadStructurePhotos(editId, photoFiles);
        clearUnsavedChanges();
        navigate(structureReportPath(saved.hashId, true));
      } else {
        const created = await api.post<StructureDetail>('/api/v1/structures', body);
        await uploadStructurePhotos(created.id, photoFiles);
        clearUnsavedChanges();
        navigate(structureReportPath(created.hashId, true));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save structure');
      setSubmitting(false);
    }
  };

  const familyTitle =
    family === 'precast'
      ? 'Precast Concrete Structure with Grate\u00a0Configuration'
      : family === 'curbthroat'
        ? 'Open Throat Curb Inlet\u00a0Configuration'
        : 'Cast Iron Frame and Grate\u00a0Configuration';

  // Legacy parity: when a family has multiple subtypes (Cast Iron), the subtype
  // selection is its own screen ("… Selection") and, once a subtype is chosen,
  // the dedicated configuration form replaces the tiles instead of appearing
  // beneath them.
  const familySelectionTitle =
    family === 'castiron'
      ? 'Cast Iron Frame and Grate\u00a0Selection'
      : familyTitle;
  const showSelection = tiles.length > 1 && !selectedTile;

  return (
    <div className="container-fluid" data-unsaved-guard>
      <div className="row mt-lg-3 mt-1">
        <div className="col col-xl-12 col-lg-12">
          <div className="container-fluid">
            {showSelection && (
              <>
                <div className="row justify-content-center">
                  <div className="col-12 text-center">
                    <h1>{familySelectionTitle}</h1>
                  </div>
                </div>

                <div className="row justify-content-center mb-3">
                  {tiles.map((tile) => (
                    <div
                      key={tile.type.id}
                      className="col-12 col-md-6 col-xl-3 p-2 d-grid text-center"
                    >
                      <a
                        className="text-reset"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedTypeId(tile.type.id)}
                      >
                        <img
                          className="border mx-auto img-fluid"
                          src={tile.image}
                          alt={tile.label}
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.display = 'none';
                          }}
                        />
                        <div className="d-flex align-items-center" style={{ height: '60px' }}>
                          <span className="fs-2 flex-fill text-center">{tile.label}</span>
                        </div>
                      </a>

                      <HoverTooltip
                        text={`Click here to view ${tile.label.replace(/\u00a0/g, ' ')} dimensional drawings.`}
                        maxWidth={260}
                      >
                        <span
                          style={{ cursor: 'pointer', display: 'block' }}
                          onClick={() => setInfoTypeId(tile.type.id)}
                        >
                          <a className="btn btn-outline-light p-0">
                            <div className="d-flex flex-row pb-2">
                              <div className="w-100 text-start ps-1">
                                <img
                                  className="p-1"
                                  src="/images/ICON/Icon_Transform_Small.png"
                                  alt=""
                                  style={{ display: 'inline-block', verticalAlign: 'middle' }}
                                  onError={(e) => {
                                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                                <br />
                                <span className="p-1">More Information</span>
                              </div>
                              <div className="flex-shrink-1 text-end">
                                <img
                                  className="p-2"
                                  src="/images/ICON/Icon_Info_Small.png"
                                  alt=""
                                  style={{ display: 'inline-block', verticalAlign: 'middle' }}
                                  onError={(e) => {
                                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              </div>
                            </div>
                          </a>
                        </span>
                      </HoverTooltip>
                    </div>
                  ))}
                </div>

                <div className="row justify-content-center">
                  <div className="col-12 text-center">
                    <Link
                      to="/newstructure"
                      className="btn btn-sm mt-2 py-1 btn-outline-secondary"
                    >
                      Restart Configuration
                    </Link>
                  </div>
                </div>

                {infoTile && (
                  <SubtypeInfoModal
                    type={infoTile.type}
                    onClose={() => setInfoTypeId(null)}
                  />
                )}
              </>
            )}

            {selectedTile && (
              <>
                <div className="row justify-content-center">
                  <div className="col-12 text-center">
                    <h1>{configTitleFor(selectedTile.type)}</h1>
                    <p>
                      Enter your dimensions to the nearest tenth of an inch.
                      <br />
                      Please note: structure and filter will be built to the exact dimensions provided. Please ensure the dimensions provided are as accurate as&nbsp;possible.
                    </p>
                  </div>
                </div>

                <div className="row justify-content-center">
                <div className="col-lg-6 p-0 pe-lg-5 py-3 py-md-0 text-center">
                  {(() => {
                    const slug = diagramSlugFor(selectedTile.type);
                    if (!slug) return null;
                    return (
                      <>
                        <object
                          key={`${slug}-desktop`}
                          ref={svgRef}
                          type="image/svg+xml"
                          className="d-none d-lg-block mx-auto"
                          data={`/images/DRAWINGS/${slug}.svg`}
                          aria-label={`${familyTitle} measurement diagram`}
                          onLoad={writeOutputs}
                        />
                        <object
                          key={`${slug}-mobile`}
                          ref={svgMobileRef}
                          type="image/svg+xml"
                          className="d-block d-lg-none mx-auto"
                          data={`/images/DRAWINGS/${slug}-Mobile.svg`}
                          aria-label={`${familyTitle} measurement diagram`}
                          onLoad={writeOutputs}
                        />
                      </>
                    );
                  })()}
                </div>

                <div className="col-12 col-lg-6 border-lg-start ps-lg-5 text-start">
                  {!showExtras && (<>
                  <div className="mb-2">
                    <label className="form-label fs-5" htmlFor="StructureNickname">
                      Structure Name (Optional)
                      <HoverTooltip
                        text="Give your structure a name to find it easier in the future. Locations, structure purpose, or dates all make good names."
                        maxWidth={260}
                      >
                        <img
                          className="ps-2"
                          src="/images/ICON/Icon_Info_Green_Filled_Small.png"
                          alt=""
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </HoverTooltip>
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="StructureNickname"
                      id="StructureNickname"
                      placeholder="Structure Name"
                      maxLength={120}
                      value={nickname}
                      onChange={(e) => setNickname(e.target.value)}
                    />
                  </div>

                  {enabledFields.map((f) => (
                    <div className="mb-2" key={f.key}>
                      <label
                        className="form-label fs-5"
                        htmlFor={f.key}
                        onMouseEnter={() => highlightField(f.svgId)}
                      >
                        {f.label}
                        <HoverTooltip
                          text="For clarifications regarding measurements, reference the accompanying structural detail drawing"
                          maxWidth={260}
                        >
                          <img
                            className="ps-2"
                            src="/images/ICON/Icon_Info_Green_Filled_Small.png"
                            alt=""
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </HoverTooltip>
                      </label>
                      <input
                        type="number"
                        step="0.001"
                        min={f.min}
                        className="form-control form-control-sm"
                        name={f.key}
                        id={f.key}
                        placeholder="Inches*"
                        value={(values[f.key] as number | undefined) ?? ''}
                        onFocus={() => highlightField(f.svgId)}
                        onMouseEnter={() => highlightField(f.svgId)}
                        onChange={(e) =>
                          setValues((prev) => ({
                            ...prev,
                            [f.key]:
                              e.target.value === '' ? undefined : Number(e.target.value),
                          }))
                        }
                        onBlur={(e) => {
                          if (e.target.value === '' || f.min <= 0) return;
                          const n = Number(e.target.value);
                          if (!Number.isFinite(n) || n >= f.min) return;
                          setValues((prev) => ({ ...prev, [f.key]: f.min }));
                          setMintriggered(true);
                        }}
                        required
                      />
                    </div>
                  ))}

                  {supportsHeavyDuty && (
                    <div className="mb-2">
                      <label className="form-label fs-5">
                        Grate Style
                        <HoverTooltip text="What type of grate will your structure need">
                          <img
                            className="ps-2"
                            src="/images/ICON/Icon_Info_Green_Filled_Small.png"
                            alt=""
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </HoverTooltip>
                      </label>
                      <div className="row justify-content-center">
                        <div className="col-12 col-lg-4 d-grid pt-1 gap-2">
                          <input
                            type="radio"
                            className="btn-check"
                            name="grate_style"
                            id="grate_style_flat_bottom"
                            autoComplete="off"
                            checked={heavyDuty === false}
                            onChange={() => setHeavyDuty(false)}
                          />
                          <label
                            className="btn p-1 btn-outline-primary"
                            htmlFor="grate_style_flat_bottom"
                          >
                            Flat Bottom
                          </label>
                        </div>
                        <div className="col-12 col-lg-8 d-grid pt-1 gap-2">
                          <input
                            type="radio"
                            className="btn-check"
                            name="grate_style"
                            id="grate_style_extended_bottom"
                            autoComplete="off"
                            checked={heavyDuty === true}
                            onChange={() => setHeavyDuty(true)}
                          />
                          <label
                            className="btn p-1 btn-outline-primary"
                            htmlFor="grate_style_extended_bottom"
                          >
                            Extended Bottom (Heavy Duty)
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  {heavyDuty === true && (
                    <>
                      <div className="mb-2">
                        <label className="form-label fs-5" htmlFor="extended_bottom_depth">
                          Extended Bottom Depth
                          <img
                            className="ps-2"
                            src="/images/ICON/Icon_Info_Green_Filled_Small.png"
                            alt=""
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.001"
                          className="form-control form-control-sm"
                          name="extended_bottom_depth"
                          id="extended_bottom_depth"
                          placeholder="Inches*"
                          value={extendedBottomDepth}
                          onChange={(e) =>
                            setExtendedBottomDepth(
                              e.target.value === '' ? '' : Number(e.target.value),
                            )
                          }
                          required
                        />
                      </div>
                      <div className="mb-2">
                        <label className="form-label fs-5" htmlFor="extended_bottom_width">
                          Extended Bottom Width
                          <img
                            className="ps-2"
                            src="/images/ICON/Icon_Info_Green_Filled_Small.png"
                            alt=""
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.001"
                          className="form-control form-control-sm"
                          name="extended_bottom_width"
                          id="extended_bottom_width"
                          placeholder="Inches*"
                          value={extendedBottomWidth}
                          onChange={(e) =>
                            setExtendedBottomWidth(
                              e.target.value === '' ? '' : Number(e.target.value),
                            )
                          }
                          required
                        />
                      </div>
                    </>
                  )}

                  {mintriggered && (
                    <div className="alert alert-danger fade show" role="alert">
                      A value entered did not meet the minimum required value. This value has been automatically set to the minimum value allowed. Please double check that the form is accurate before continuing
                    </div>
                  )}

                  {crossFieldErrors.map((msg) => (
                    <div key={msg} className="alert alert-danger fade show" role="alert">
                      {msg}
                    </div>
                  ))}
                  </>)}

                  {showExtras && (
                    <StructureNotesPhotosFields
                      notes={notes}
                      onNotesChange={setNotes}
                      files={photoFiles}
                      onFilesChange={setPhotoFiles}
                      disabled={submitting}
                    />
                  )}

                  {error && (
                    <div className="alert alert-warning fade show" role="alert">
                      {error}
                    </div>
                  )}

                  <div className="mb-2 text-center">
                    <button
                      type="button"
                      onClick={() => {
                        if (!showExtras) {
                          if (!canSubmit) return;
                          setShowExtras(true);
                        } else {
                          submit();
                        }
                      }}
                      disabled={!canSubmit}
                      className="btn btn-sm mt-2 py-1 btn-primary"
                    >
                      {submitting
                        ? 'Saving…'
                        : showExtras
                          ? isEdit
                            ? 'Save changes'
                            : 'Submit'
                          : 'Continue'}
                    </button>
                  </div>
                </div>

                <div className="col-12 text-center">
                  <Link
                    to="/newstructure"
                    className="btn btn-sm mt-2 py-1 btn-outline-secondary"
                  >
                    Restart Configuration
                  </Link>
                </div>
              </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
